import {ACTES, CARTE_FONDS, CARTE_STYLE_DEFAUT, CARTE_STYLES, VERDICTS} from './tokens.js';

// F10 : état complet de la vue <-> query string (handoff §4). JS pur.
// Clés et ordre figés :
export const CLES = ['patrimoines', 'acte', 'du', 'au', 'commune', 'etats', 'element', 'style', 'fond', 'communes', 'circuits', 'collecte', 'at', 'z', 'c'];

const ACTE_DEFAUT = 'maint';
const RE_DATE = /^\d{4}-\d{2}-\d{2}$/;

function dateValide(v) {
    if (typeof v !== 'string' || !RE_DATE.test(v)) return false;
    const d = new Date(`${v}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}

function groupes(index) {
    const res = {intra: [], extra: []};
    for (const p of index.patrimoineById.values()) {
        if (res[p.groupe]) res[p.groupe].push(p.id);
    }
    return res;
}

function tousPatrimoines(index) {
    return [...index.patrimoineById.keys()].sort((a, b) => a - b);
}

/** État par défaut de la vue (équivalent de `fromQuery('')`). */
export function etatParDefaut(index, aujourdhui) {
    return {
        patrimoines: tousPatrimoines(index),
        acte: ACTE_DEFAUT,
        du: '',
        au: aujourdhui,
        commune: '',
        etats: [...VERDICTS],
        element: '',
        style: CARTE_STYLE_DEFAUT,
        fond: 'plan',
        communes: true,
        circuits: false,
        collecte: !!index.collecte,
        at: aujourdhui,
        z: null,
        c: null,
    };
}

function lireListe(valeur) {
    return valeur === '' ? [] : valeur.split(',').map((s) => s.trim()).filter(Boolean);
}

function lirePatrimoines(valeur, index, defaut) {
    if (valeur === null) return defaut;
    const jetons = lireListe(valeur);
    if (jetons.length === 0) return []; // « tout décoché » : présent mais vide
    const g = groupes(index);
    const ids = new Set();
    for (const j of jetons) {
        if (j === 'intra' || j === 'extra') g[j].forEach((id) => ids.add(id));
        else if (/^\d+$/.test(j) && index.patrimoineById.has(Number(j))) ids.add(Number(j));
    }
    return ids.size > 0 ? [...ids].sort((a, b) => a - b) : defaut;
}

function lireEtats(valeur, defaut) {
    if (valeur === null) return defaut;
    const jetons = lireListe(valeur);
    if (jetons.length === 0) return [];
    const connus = VERDICTS.filter((v) => jetons.includes(v)); // ordre canonique
    return connus.length > 0 ? connus : defaut;
}

function lireCentre(valeur) {
    if (!valeur) return null;
    const m = /^(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)$/.exec(valeur);
    if (!m) return null;
    const lat = Number(m[1]);
    const lng = Number(m[2]);
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    return {lat, lng};
}

function lireZoom(valeur) {
    if (!valeur || !/^\d{1,2}$/.test(valeur)) return null;
    return Number(valeur);
}

/**
 * `search` avec ou sans « ? » initial. Une valeur inconnue retombe sur le défaut, sans exception.
 * `collecteDefaut` : valeur de `collecte` quand la clé est absente (bouton mémorisé en localStorage, cf. collecte.js).
 * Sans collecte, l'acte `collecte` et le style `tonnage` retombent sur leurs défauts (acte non affiché).
 */
export function fromQuery(search, index, aujourdhui, collecteDefaut = false) {
    const defaut = {...etatParDefaut(index, aujourdhui), collecte: !!collecteDefaut};
    let p;
    try {
        p = new URLSearchParams(typeof search === 'string' ? search : '');
    } catch (e) {
        return defaut;
    }
    const get = (k) => (p.has(k) ? p.get(k) : null);

    const acte = get('acte');
    const du = get('du');
    const au = get('au');
    const at = get('at');
    const style = get('style');
    const fond = get('fond');
    const communes = get('communes');
    const circuits = get('circuits');
    const collecte = get('collecte') === '1' ? true : get('collecte') === '0' ? false : defaut.collecte;

    const auFinal = dateValide(au) ? au : defaut.au;
    const duLu = du === '' || dateValide(du) ? (du || '') : defaut.du;
    const commune = get('commune');
    const element = get('element');

    return {
        patrimoines: lirePatrimoines(get('patrimoines'), index, defaut.patrimoines),
        acte: ACTES.includes(acte) && (collecte || acte !== 'collecte') ? acte : defaut.acte,
        du: duLu !== '' && duLu > auFinal ? '' : duLu,
        au: auFinal,
        // Commune (clé) ou élément inconnus de l'index : retour au défaut.
        commune: commune && index.communeParCle.has(commune) ? commune : '',
        etats: lireEtats(get('etats'), defaut.etats),
        element: element && index.elements.has(element) ? element : '',
        style: CARTE_STYLES.includes(style) && (style !== 'tonnage' || (collecte && acte === 'collecte')) ? style : defaut.style,
        fond: CARTE_FONDS.includes(fond) ? fond : defaut.fond,
        communes: communes === '0' ? false : communes === '1' ? true : defaut.communes,
        circuits: circuits === '1' ? true : circuits === '0' ? false : defaut.circuits,
        collecte,
        at: dateValide(at) ? at : defaut.at,
        z: lireZoom(get('z')),
        c: lireCentre(get('c')),
    };
}

function ecrirePatrimoines(ids, index) {
    if (!index) return [...ids].sort((a, b) => a - b).join(',');
    const choisis = new Set(ids);
    const g = groupes(index);
    const jetons = [];
    const restants = new Set(choisis);
    for (const nom of ['intra', 'extra']) {
        if (g[nom].length > 0 && g[nom].every((id) => choisis.has(id))) {
            jetons.push(nom);
            g[nom].forEach((id) => restants.delete(id));
        }
    }
    return [...jetons, ...[...restants].sort((a, b) => a - b)].join(',');
}

/** Sérialise sans « ? » initial. Toutes les clés sont écrites, dans l'ordre de `CLES`. */
export function toQuery(etatVue, index) {
    const centre = etatVue.c ? `${etatVue.c.lat.toFixed(5)},${etatVue.c.lng.toFixed(5)}` : '';
    const valeurs = {
        patrimoines: ecrirePatrimoines(etatVue.patrimoines, index),
        acte: etatVue.acte,
        du: etatVue.du || '',
        au: etatVue.au || '',
        commune: etatVue.commune || '',
        etats: VERDICTS.filter((v) => etatVue.etats.includes(v)).join(','),
        element: etatVue.element || '',
        style: etatVue.style,
        fond: etatVue.fond,
        communes: etatVue.communes ? '1' : '0',
        circuits: etatVue.circuits ? '1' : '0',
        collecte: etatVue.collecte ? '1' : '0',
        at: etatVue.at || '',
        z: etatVue.z == null ? '' : String(Math.round(etatVue.z)),
        c: centre,
    };
    // Les listes gardent leurs virgules lisibles ; seuls les textes libres sont encodés.
    return CLES.map((k) => {
        const v = valeurs[k];
        return `${k}=${k === 'commune' || k === 'element' ? encodeURIComponent(v) : v}`;
    }).join('&');
}
